import { request } from "../helpers/axios.new";
import AxiosConfigs from "../configs/axios";

// Playbook talks to REACT_APP_PLAYBOOK_HOST when set (the deployed API does
// not have /api/playbook yet — every call there 404s, which breaks YETI).
// Once the server is redeployed with the playbook routes, remove the override
// from .env.local and this falls back to the shared host automatically.
const apiUrl = AxiosConfigs.REACT_APP_PLAYBOOK_HOST || AxiosConfigs.REACT_APP_LINK_HOST;
const base = `${apiUrl}/api/playbook`;

/* ----------------------------- Library CRUD ----------------------------- */
// Absolute URLs so axios uses `playbookHost` and ignores the shared baseURL.

export const GET_PLAYBOOKS = () =>
  request({ url: base, method: "GET" });

export const GET_PLAYBOOK = (id) =>
  request({ url: `${base}/${id}`, method: "GET" });

export const CREATE_PLAYBOOK = (data) =>
  request({ url: base, method: "POST", data });

export const UPDATE_PLAYBOOK = (id, data) =>
  request({ url: `${base}/${id}`, method: "PUT", data });

export const DELETE_PLAYBOOK = (id) =>
  request({ url: `${base}/${id}`, method: "DELETE" });

/* ------------------------------ YETI (AI) ------------------------------- */

// Multi-page generation, step 1: outline (sections + page titles).
export const YETI_OUTLINE = (data) =>
  request({ url: `${base}/ai/outline`, method: "POST", data });

// Multi-page generation, step 2: write one page's HTML. Called per page.
export const YETI_PAGE = (data) =>
  request({ url: `${base}/ai/page`, method: "POST", data });

// Inline highlight edits — small, non-streaming.
export const YETI_INLINE = (data) =>
  request({ url: `${base}/ai/inline`, method: "POST", data });

/* ------------------------------ Images ---------------------------------- */

// Upload an image; returns { key } which resolves to a URL via GET_IMAGE_URL.
export const EXTRACT_DOCUMENT = (file) => {
  const form = new FormData();
  form.append("file", file);
  return request({
    url: `${base}/extract`,
    method: "POST",
    data: form,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const UPLOAD_PLAYBOOK_IMAGE = (file) => {
  const form = new FormData();
  form.append("image", file);
  return request({
    url: `${base}/upload`,
    method: "POST",
    data: form,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * Stream a YETI response (generate | assist). Calls onChunk(textSoFar, delta)
 * as HTML arrives so the editor can render live. Resolves with the full text.
 * Uses fetch (not axios) for a readable streaming body.
 */
const streamYeti = async (path, body, onChunk) => {
  const token = localStorage.getItem("token");
  const res = await fetch(`${base}/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok || !res.body) {
    let message = `YETI request failed (${res.status})`;
    try {
      const err = await res.json();
      message = err?.error?.message || message;
    } catch (e) {
      /* non-JSON error body */
    }
    throw new Error(message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let full = "";
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    const delta = decoder.decode(value, { stream: true });
    full += delta;
    if (onChunk) onChunk(full, delta);
  }
  return full;
};

export const YETI_GENERATE = (body, onChunk) =>
  streamYeti("ai/generate", body, onChunk);

export const YETI_ASSIST = (body, onChunk) =>
  streamYeti("ai/assist", body, onChunk);

/**
 * Conversational planning. YETI plays the same role the main Claude chat does:
 * it reads the whole conversation and streams back a plain-text reply — a
 * clarifying question, a suggestion, or a short outline it proposes to write.
 * The admin keeps chatting until they're happy, then the outline pipeline
 * (YETI_OUTLINE → YETI_PAGE) runs on the full transcript.
 *
 * body: { messages: [{ role: "user" | "assistant", content }], title?, type? }
 * Streams text (not HTML). Backend: POST /api/playbook/ai/plan (see dev spec).
 */
export const YETI_CHAT = (body, onChunk) =>
  streamYeti("ai/plan", body, onChunk);
